import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'

export interface NotificationItem {
  id: string
  category: string
  /** Focus/role the notification applies to (e.g. `candidate`, `employer`). */
  audience: string
  title: string
  body: string
  action_url: string
  metadata: Record<string, unknown>
  read_at: string | null
  created_at: string
}

export interface NotificationListResponse {
  items: NotificationItem[]
  total: number
  unread_count: number
}

export interface NotificationPreferenceItem {
  category: string
  label: string
  description: string
  audience: string
  email_enabled: boolean
  in_app_enabled: boolean
}

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const

async function throwOnError(response: Response): Promise<void> {
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Notifications request failed'))
  }
}

export function useNotifications(options?: { limit?: number; unreadOnly?: boolean }) {
  const { isAuthenticated } = useAuth()
  const limit = options?.limit ?? 20
  const unreadOnly = options?.unreadOnly ?? false

  return useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'list', { limit, unreadOnly }],
    queryFn: async (): Promise<NotificationListResponse> => {
      const params = new URLSearchParams({ limit: String(limit) })
      if (unreadOnly) params.set('unread', 'true')
      const response = await authorizedFetch(`/api/notifications/?${params}`)
      await throwOnError(response)
      return response.json()
    },
    enabled: isAuthenticated,
  })
}

const NOTIFICATIONS_PAGE_SIZE = 25

/** Paged history for the /notifications page ("Load more" pagination). */
export function useInfiniteNotifications(options?: { unreadOnly?: boolean }) {
  const { isAuthenticated } = useAuth()
  const unreadOnly = options?.unreadOnly ?? false

  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'infinite', { unreadOnly }],
    queryFn: async ({ pageParam }): Promise<NotificationListResponse> => {
      const params = new URLSearchParams({
        limit: String(NOTIFICATIONS_PAGE_SIZE),
        offset: String(pageParam),
      })
      if (unreadOnly) params.set('unread', 'true')
      const response = await authorizedFetch(`/api/notifications/?${params}`)
      await throwOnError(response)
      return response.json()
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    enabled: isAuthenticated,
  })
}

export function useUnreadNotificationCount() {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'unread-count'],
    queryFn: async (): Promise<number> => {
      const response = await authorizedFetch('/api/notifications/unread-count')
      await throwOnError(response)
      const data = (await response.json()) as { unread_count: number }
      return data.unread_count
    },
    enabled: isAuthenticated,
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string): Promise<void> => {
      const response = await authorizedFetch(
        `/api/notifications/${notificationId}/read`,
        { method: 'POST' }
      )
      await throwOnError(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await authorizedFetch('/api/notifications/read-all', {
        method: 'POST',
      })
      await throwOnError(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    },
  })
}

export function useNotificationPreferences() {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'preferences'],
    queryFn: async (): Promise<NotificationPreferenceItem[]> => {
      const response = await authorizedFetch('/api/notifications/preferences')
      await throwOnError(response)
      return response.json()
    },
    enabled: isAuthenticated,
  })

  const mutation = useMutation({
    mutationFn: async (
      preferences: Array<
        Pick<NotificationPreferenceItem, 'category' | 'email_enabled' | 'in_app_enabled'>
      >
    ): Promise<NotificationPreferenceItem[]> => {
      const response = await authorizedFetch('/api/notifications/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences }),
      })
      await throwOnError(response)
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.setQueryData([...NOTIFICATIONS_QUERY_KEY, 'preferences'], data)
    },
  })

  return {
    preferences: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    updatePreferences: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  }
}
